import { useState, useEffect, useContext } from "react";
import { C, LOGO_BASE, THUMB_BASE, ATTACH_BASE } from "./utils";
import { ThemeCtx } from "./AppShell";
import CommentsPanel from "./CommentsPanel";

const isImage  = p => /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(p||"");
const fileName = p => (p||"").split("/").pop();
const attachUrl= p => ATTACH_BASE + p;
const thumbUrl = p => THUMB_BASE  + fileName(p);
const stripHtml= h => h ? h.replace(/<[^>]*>/g," ").replace(/\s+/g," ").trim() : "";

const PHASE_CONFIG = {
  "Approved":  { color:"#059669", bg:"#D1FAE5" },
  "Phase III": { color:"#1D4ED8", bg:"#DBEAFE" },
  "Phase II":  { color:"#7C3AED", bg:"#EDE9FE" },
  "Phase I":   { color:"#0891B2", bg:"#CFFAFE" },
};
const phaseCfg = p => PHASE_CONFIG[p] || { color:"#6B7280", bg:"#F3F4F6" };

// ─── Spinner ──────────────────────────────────────────────────────────────────
function Spin({ color=C.primary }) {
  return (
    <div style={{ display:"flex",alignItems:"center",justifyContent:"center",
      padding:"4rem",gap:"10px",color:"#9CA3AF",fontSize:"14px" }}>
      <div style={{ width:"16px",height:"16px",border:`2px solid #E5E7EB`,
        borderTopColor:color,borderRadius:"50%",animation:"_pps 0.8s linear infinite" }}/>
      <style>{"@keyframes _pps{to{transform:rotate(360deg)}}"}</style>
    </div>
  );
}

// ─── FilePill ─────────────────────────────────────────────────────────────────
function FilePill({ filepath }) {
  const [hov,setHov] = useState(false);
  const name = fileName(filepath).replace(/_\d{2}_\d{2}_\d{4}_\d{2}_\d{2}\./,".");
  const ext  = name.split(".").pop().toUpperCase();
  const isImg= isImage(filepath);
  const ec   = isImg?"#059669":ext==="PDF"?"#DC2626":ext==="PPTX"||ext==="PPT"?"#D97706":
               ext==="DOCX"||ext==="DOC"?"#2563EB":"#6B7280";
  const em   = isImg?"🖼":ext==="PDF"?"📄":ext==="PPTX"||ext==="PPT"?"📊":
               ext==="DOCX"?"📝":"📎";
  return (
    <div onClick={()=>window.open(attachUrl(filepath),"_blank","noopener,noreferrer")}
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{ display:"inline-flex",alignItems:"center",gap:"4px",
        padding:"3px 9px",borderRadius:"7px",cursor:"pointer",
        background:hov?`${ec}10`:"#fff",
        border:`1px solid ${hov?ec:ec+"44"}`,
        fontSize:"11px",color:"#374151",transition:"all 0.12s" }}>
      <span>{em}</span>
      <span style={{ maxWidth:"140px",overflow:"hidden",textOverflow:"ellipsis",
        whiteSpace:"nowrap",fontWeight:"500" }}>{name}</span>
      <span style={{ background:ec,color:"#fff",borderRadius:"4px",
        padding:"0 4px",fontSize:"8px",fontWeight:"800" }}>{ext}</span>
    </div>
  );
}

// ─── Source links parser ──────────────────────────────────────────────────────
function SourceLinks({ html }) {
  const ctx      = useContext(ThemeCtx) || {};
  const tPrimary = ctx.theme?.primary || C.primary;
  if (!html) return null;
  const parser  = new DOMParser();
  const doc     = parser.parseFromString(html, "text/html");
  const anchors = Array.from(doc.querySelectorAll("a"));
  if (anchors.length > 0) {
    const links = anchors.map(a => {
      const href = a.getAttribute("href")||"";
      if (!href || href.startsWith("blob:")) return null;
      const inner = a.textContent.trim();
      let label = inner && inner.toLowerCase()!=="link" ? inner : "";
      if (!label) {
        let prev = a.previousSibling;
        while(prev) {
          const t = (prev.textContent||"").trim()
            .replace(/^[\s,()–-]+/,"").replace(/[\s,()–-]+$/,"").trim();
          if(t){label=t;break;} prev=prev.previousSibling;
        }
      }
      return { href, label: label||"Link" };
    }).filter(Boolean);
    return (
      <div style={{ display:"flex",flexWrap:"wrap",gap:"5px" }}>
        {links.map((lnk,i)=>(
          <a key={i} href={lnk.href} target="_blank" rel="noopener noreferrer"
            style={{ display:"inline-flex",alignItems:"center",gap:"3px",
              padding:"2px 8px",borderRadius:"5px",fontSize:"11px",fontWeight:"600",
              color:tPrimary,background:`${tPrimary}10`,
              border:`1px solid ${tPrimary}30`,textDecoration:"none" }}>
            🔗 {lnk.label}
          </a>
        ))}
      </div>
    );
  }
  // No anchor tags — handle plain text (possibly comma-separated URLs)
  const plain = decodeURIComponent(doc.body.textContent.trim());
  if (!plain) return null;

  // Split on comma that is followed by a URL
  const parts = plain.split(/,\s*(?=https?:\/\/)/).map(s => s.trim()).filter(Boolean);

  if (parts.length > 1) {
    // Multiple plain URLs
    return (
      <div style={{ display:"flex",flexWrap:"wrap",gap:"5px" }}>
        {parts.map((href,i) => (
          <a key={i} href={href} target="_blank" rel="noopener noreferrer"
            style={{ display:"inline-flex",alignItems:"center",gap:"3px",
              padding:"2px 8px",borderRadius:"5px",fontSize:"11px",fontWeight:"600",
              color:tPrimary,background:`${tPrimary}10`,
              border:`1px solid ${tPrimary}30`,textDecoration:"none" }}>
            🔗 Source {i+1}
          </a>
        ))}
      </div>
    );
  }

  // Single value
  return /^https?:\/\//.test(plain)
    ? <a href={plain} target="_blank" rel="noopener noreferrer"
        style={{ display:"inline-flex",alignItems:"center",gap:"3px",
          padding:"2px 8px",borderRadius:"5px",fontSize:"11px",fontWeight:"600",
          color:tPrimary,background:`${tPrimary}10`,
          border:`1px solid ${tPrimary}30`,textDecoration:"none" }}>
        🔗 Source
      </a>
    : <span style={{ fontSize:"11px",color:"#374151" }}>{plain}</span>;
}

// ─── Image strip ──────────────────────────────────────────────────────────────
function ImageStrip({ images, compact=false }) {
  const [active,setActive] = useState(0);
  const [lb,setLb]         = useState(false);
  const tPrimary = useContext(ThemeCtx)?.theme?.primary || C.primary;
  if (!images.length) return null;
  const h = compact ? 90 : 155;
  return (
    <div>
      <div onClick={()=>setLb(true)}
        style={{borderRadius:compact?"8px 8px 0 0":"8px",overflow:"hidden",background:"rgba(0,0,0,0.2)",border:"none",
          cursor:"zoom-in",marginBottom:compact?0:"6px",position:"relative",height:h,
          display:"flex",alignItems:"center",justifyContent:"center"}}>
        <img src={attachUrl(images[active].FILEPATH)} alt="" onError={e=>e.target.style.display="none"}
          style={{maxWidth:"100%",maxHeight:`${h}px`,objectFit:"cover",width:"100%"}}/>
        {images.length>1&&<div style={{position:"absolute",bottom:"5px",right:"7px",
          background:"rgba(0,0,0,0.45)",borderRadius:"4px",padding:"1px 6px",fontSize:"9px",color:"#fff"}}>
          {active+1}/{images.length}</div>}
        {active>0&&<button onClick={e=>{e.stopPropagation();setActive(a=>a-1);}}
          style={{position:"absolute",left:"4px",width:"22px",height:"22px",borderRadius:"50%",
            border:"none",background:"rgba(0,0,0,0.4)",color:"#fff",fontSize:"13px",cursor:"pointer"}}>‹</button>}
        {active<images.length-1&&<button onClick={e=>{e.stopPropagation();setActive(a=>a+1);}}
          style={{position:"absolute",right:"4px",width:"22px",height:"22px",borderRadius:"50%",
            border:"none",background:"rgba(0,0,0,0.4)",color:"#fff",fontSize:"13px",cursor:"pointer"}}>›</button>}
      </div>
      {!compact&&images.length>1&&<div style={{display:"flex",gap:"4px",overflowX:"auto",scrollbarWidth:"none"}}>
        {images.map((img,j)=>(
          <div key={j} onClick={()=>setActive(j)}
            style={{flexShrink:0,width:"38px",height:"38px",borderRadius:"5px",overflow:"hidden",
              cursor:"pointer",border:`2px solid ${active===j?tPrimary:"transparent"}`,
              opacity:active===j?1:0.45,transition:"all 0.12s"}}>
            <img src={thumbUrl(img.FILEPATH)} alt="" onError={e=>e.target.style.display="none"}
              style={{width:"100%",height:"100%",objectFit:"cover"}}/>
          </div>
        ))}
      </div>}
      {lb&&(
        <div onClick={()=>setLb(false)} style={{position:"fixed",inset:0,zIndex:99999,
          background:"rgba(0,0,0,0.92)",display:"flex",alignItems:"center",justifyContent:"center"}}>
          {active>0&&<button onClick={e=>{e.stopPropagation();setActive(a=>a-1);}}
            style={{position:"absolute",left:"16px",width:"40px",height:"40px",borderRadius:"50%",
              border:"none",background:"rgba(255,255,255,0.15)",color:"#fff",fontSize:"22px",cursor:"pointer"}}>‹</button>}
          <img src={attachUrl(images[active].FILEPATH)} alt="" onClick={e=>e.stopPropagation()}
            style={{maxWidth:"90vw",maxHeight:"90vh",objectFit:"contain",borderRadius:"8px"}}/>
          {active<images.length-1&&<button onClick={e=>{e.stopPropagation();setActive(a=>a+1);}}
            style={{position:"absolute",right:"16px",width:"40px",height:"40px",borderRadius:"50%",
              border:"none",background:"rgba(255,255,255,0.15)",color:"#fff",fontSize:"22px",cursor:"pointer"}}>›</button>}
          <button onClick={()=>setLb(false)} style={{position:"absolute",top:"16px",right:"16px",
            width:"36px",height:"36px",borderRadius:"50%",border:"none",
            background:"rgba(255,255,255,0.15)",color:"#fff",fontSize:"20px",cursor:"pointer"}}>×</button>
        </div>
      )}
    </div>
  );
}

function DetailRow({ d, index, defaultOpen, subId }) {
  const [open, setOpen] = useState(defaultOpen||false);
  const ctx      = useContext(ThemeCtx) || {};
  const theme    = ctx.theme || {};
  const tPrimary = theme.primary || C.primary;
  const pc      = phaseCfg(d.PHASENAME);
  const chatbot = Array.isArray(d.CHATBOT)?d.CHATBOT:[];
  const images  = chatbot.filter(a=>isImage(a.FILEPATH));
  const files   = chatbot.filter(a=>!isImage(a.FILEPATH));
  const notes   = stripHtml(d.NOTES||d.MESSAGES||"");
  const country = d.REGIONNAME||d.COUNTRYNAME||"";

  const openNewTab = (e) => {
    e.stopPropagation();
    if (!d.DOCUMENTID) return;
    const base = window.location.origin+window.location.pathname;
    window.open(`${base}?doc=${d.DOCUMENTID}&dt=${d.DOCTYPEID||98}&sub=${subId||""}`,"_blank","noopener,noreferrer");
  };

  return (
    <div style={{ borderBottom:"1px solid #F1F5F9" }}>
      {/* Row header — grid aligned with column headers */}
      <div onClick={()=>setOpen(o=>!o)}
        style={{ display:"grid",gridTemplateColumns:"92px 1fr 120px 28px",
          gap:"6px",alignItems:"center",padding:"9px 14px",
          cursor:"pointer",transition:"background 0.1s",
          background:open?`${pc.color}08`:"transparent" }}>

        <span style={{ fontSize:"10px",fontWeight:"700",padding:"2px 7px",
          borderRadius:"5px",color:pc.color,background:pc.bg,
          overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
          display:"inline-block" }}>
          {d.PHASENAME||"—"}
        </span>

        <span style={{ fontSize:"12px",fontWeight:"600",color:"#111827",
          overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>
          {d.INDICATIONNAME||d.BRANDNAME||"—"}
        </span>

        {d.ACRONYMS
          ? <span
              onClick={d.ACRONYMSID ? e=>{e.stopPropagation();
                const base=window.location.origin+window.location.pathname;
                window.open(`${base}?doc=${d.ACRONYMSID}&dt=96&sub=${subId}`,"_blank","noopener,noreferrer");
              } : undefined}
              title={d.ACRONYMSID?"Open clinical trial":""}
              style={{ fontSize:"11px",color:tPrimary,
                overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
                cursor:d.ACRONYMSID?"pointer":"default",
                textDecoration:d.ACRONYMSID?"underline":"none",
                textDecorationStyle:"dotted" }}>
              {d.ACRONYMS}
            </span>
          : <span/>
        }

        <div style={{ display:"flex",alignItems:"center",gap:"3px",justifyContent:"flex-end" }}>
          {d.DOCUMENTID&&<span onClick={openNewTab}
            title="Open full detail in new tab"
            style={{ fontSize:"13px",color:"#fff",cursor:"pointer",lineHeight:1,
              background:"#185FA5",borderRadius:"4px",padding:"2px 5px",
              fontWeight:"700",opacity:0.9,transition:"opacity 0.15s" }}
            onMouseEnter={e=>e.target.style.opacity="1"}
            onMouseLeave={e=>e.target.style.opacity="0.9"}>↗</span>}
          <span style={{ fontSize:"11px",color:"#9CA3AF",
            transform:open?"rotate(180deg)":"none",transition:"transform 0.18s",
            display:"inline-block" }}>▼</span>
        </div>
      </div>

      {/* Expanded body */}
      {open&&(
        <div style={{ padding:"12px 14px 14px",background:"#F8FAFC",
          borderTop:"1px solid #F1F5F9" }}>
          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:"12px",
            marginBottom:notes||files.length||d.LINK?"12px":0 }}>

            {/* Left: title + meta grid */}
            <div>
              {d.TITLE&&<div style={{ fontSize:"12px",fontWeight:"600",color:"#111827",
                lineHeight:1.4,marginBottom:"10px" }}>{d.TITLE}</div>}
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:"6px" }}>
                {[
                  {label:"Trial status",       val:d.TRIALSTATUSNAME},
                  {label:"Enrollment",          val:d.TARGETN!=null
                    ?`${d.CURRENTN!=null?d.CURRENTN:"?"} / ${d.TARGETN}`:null},
                  {label:"Sponsor",             val:d.SPONSORNAME},
                  {label:"Market",              val:country},
                  {label:"Anticipated launch",  val:d.LAUNCHDATE},
                  {label:"Launch assumptions",  val:d.LAUNCHASSUMPTIONS},
                ].filter(r=>r.val).map((r,i)=>(
                  <div key={i}>
                    <div style={{ fontSize:"10px",color:"#9CA3AF",marginBottom:"1px" }}>{r.label}</div>
                    <div style={{ fontSize:"12px",color:"#374151",fontWeight:"500" }}>{r.val}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: image strip if available */}
            {images.length>0&&(
              <ImageStrip images={images}/>
            )}
          </div>

          {/* Notes */}
          {notes&&(
            <div style={{ fontSize:"12px",color:"#374151",lineHeight:1.7,
              padding:"10px 12px",borderRadius:"8px",background:"#fff",
              border:"1px solid #E5E7EB",marginBottom:"10px" }}>
              {notes}
            </div>
          )}

          {/* Files + source */}
          {(files.length>0||d.LINK)&&(
            <div style={{ display:"flex",flexWrap:"wrap",gap:"6px",alignItems:"center" }}>
              {files.map((f,i)=><FilePill key={i} filepath={f.FILEPATH}/>)}
              {d.LINK&&<SourceLinks html={d.LINK}/>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main detail page ─────────────────────────────────────────────────────────
export default function ProductProfileDetail({ product, adminBar }) {
  const [tab, setTab] = useState("attachments");
  const ctx      = useContext(ThemeCtx) || {};
  const theme    = ctx.theme || {};
  const tPrimary = theme.primary || C.primary;
  const subId    = ctx.activeSub?.SUBPROJECTID || "";
  const user     = ctx.user || null;
  const activeSub= ctx.activeSub || null;
  const docId    = new URLSearchParams(window.location.search).get("doc")||"";

  if (!product) return <Spin color={tPrimary}/>;

  const details  = Array.isArray(product.PRODUCT_DETAIL)?product.PRODUCT_DETAIL:[];
  const chatbot  = Array.isArray(product.CHATBOT)?product.CHATBOT:[];
  const images   = chatbot.filter(a=>isImage(a.FILEPATH));
  const files    = chatbot.filter(a=>!isImage(a.FILEPATH));
  const logoUrl  = product.COMPANY_LOGO?LOGO_BASE+product.COMPANY_LOGO:null;
  const phases   = [...new Set(details.map(d=>d.PHASENAME).filter(Boolean))];
  const notes    = stripHtml(product.NOTES||"");

  return (
    <div style={{ display:"flex",flexDirection:"column",height:"100%",overflow:"hidden",
      background:"#F8FAFC" }}>

      {/* ── Hero header ── */}
      <div style={{ flexShrink:0,padding:"16px 20px",
        background:`linear-gradient(135deg,${tPrimary}F0,${tPrimary}88)`,
        position:"relative",overflow:"hidden" }}>
        <div style={{ position:"absolute",right:"-20px",top:"-20px",width:"130px",
          height:"130px",borderRadius:"50%",background:"rgba(255,255,255,0.05)" }}/>

        <div style={{ display:"flex",gap:"12px",alignItems:"flex-start",position:"relative" }}>
          {/* Logo */}
          {logoUrl&&(
            <div style={{ width:"44px",height:"44px",borderRadius:"10px",flexShrink:0,
              background:"rgba(255,255,255,0.18)",border:"1.5px solid rgba(255,255,255,0.3)",
              overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center" }}>
              <img src={logoUrl} alt="" onError={e=>e.target.style.display="none"}
                style={{ width:"80%",height:"80%",objectFit:"contain" }}/>
            </div>
          )}
          <div style={{ flex:1,minWidth:0 }}>
            {/* Brand name */}
            <div style={{ fontSize:"18px",fontWeight:"800",color:"#fff",
              lineHeight:1.2,marginBottom:"5px" }}>
              {product.BRANDNAME}
            </div>
            <div style={{ display:"flex",alignItems:"center",gap:"8px",
              flexWrap:"wrap",marginBottom:"6px" }}>
              {product.GENERIC&&<span style={{ fontSize:"11px",
                color:"rgba(255,255,255,0.75)" }}>{product.GENERIC}</span>}
              {product.MOANAME&&<span style={{ fontSize:"10px",
                color:"rgba(255,255,255,0.65)" }}>· {product.MOANAME}</span>}
              {product.DELIVERY&&<span style={{ fontSize:"10px",fontWeight:"700",
                color:"rgba(255,255,255,0.9)",background:"rgba(255,255,255,0.15)",
                borderRadius:"4px",padding:"1px 7px" }}>{product.DELIVERY}</span>}
              {product.SPONSORNAME&&<span style={{ fontSize:"11px",
                color:"rgba(255,255,255,0.8)",fontWeight:"600" }}>
                · {product.SPONSORNAME}
              </span>}
            </div>
            {/* Phase pills + indication count */}
            <div style={{ display:"flex",gap:"5px",flexWrap:"wrap",alignItems:"center" }}>
              {phases.map(p=>{
                const pc=phaseCfg(p);
                return <span key={p} style={{ fontSize:"10px",fontWeight:"700",
                  padding:"2px 8px",borderRadius:"5px",
                  background:"rgba(255,255,255,0.2)",color:"#fff" }}>{p}</span>;
              })}
              {details.length>0&&<span style={{ fontSize:"10px",
                color:"rgba(255,255,255,0.65)" }}>
                · {details.length} indication{details.length!==1?"s":""}
              </span>}
            </div>
            {/* Keywords */}
            {product.KEYWORDS&&(
              <div style={{ display:"flex",flexWrap:"wrap",gap:"4px",marginTop:"4px" }}>
                {product.KEYWORDS.split(/[;,]/).map(k=>k.trim()).filter(Boolean).map((k,i)=>(
                  <span key={i} style={{ fontSize:"9px",fontWeight:"600",
                    color:"rgba(255,255,255,0.85)",
                    background:"rgba(255,255,255,0.15)",
                    borderRadius:"20px",padding:"1px 7px" }}>{k}</span>
                ))}
              </div>
            )}
          </div>
          {images.length>0&&(
            <div style={{width:"90px",flexShrink:0,borderRadius:"10px",
              overflow:"hidden",border:"2px solid rgba(255,255,255,0.25)"}}>
              <ImageStrip images={images} compact={true}/>
            </div>
          )}
        </div>
      </div>

      {/* ── AdminBar ── */}
      {adminBar&&<div style={{background:"#fff",borderBottom:"1px solid #E5E7EB",flexShrink:0}}>{adminBar}</div>}

      {/* ── Body ── */}
      <div style={{ flex:1,display:"flex",overflow:"hidden",minHeight:0 }}>

        {/* LEFT: indications table + notes */}
        <div style={{ flex:1,overflowY:"auto",padding:"16px" }}>

          {/* PRODUCT_DETAIL rows */}
          {details.length>0&&(
            <div style={{ background:"#fff",borderRadius:"12px",
              border:"1px solid #E5E7EB",overflow:"hidden",marginBottom:"14px",
              boxShadow:"0 1px 4px rgba(0,0,0,0.04)" }}>
              <div style={{ padding:"9px 14px",borderBottom:"1px solid #F1F5F9",
                display:"flex",alignItems:"center",gap:"8px",
                background:"#F8FAFC" }}>
                <span style={{ fontSize:"12px",fontWeight:"700",color:"#374151" }}>
                  Indications & details
                </span>
                <span style={{ marginLeft:"auto",fontSize:"10px",color:"#9CA3AF" }}>
                  {details.length} entr{details.length!==1?"ies":"y"}
                </span>
              </div>
              {/* Column labels */}
              <div style={{ display:"grid",gridTemplateColumns:"92px 1fr 120px 28px",
                gap:"6px",padding:"5px 14px",
                background:"#FAFAFA",borderBottom:"1px solid #F1F5F9",fontSize:"10px",
                fontWeight:"700",color:"#9CA3AF",textTransform:"uppercase",
                letterSpacing:"0.5px" }}>
                <span>Phase</span>
                <span>Indication</span>
                <span>Acronym</span>
                <span></span>
              </div>
              {details.map((d,i)=>(
                <DetailRow key={d.DOCUMENTID||i} d={d} index={i} defaultOpen={i===0} subId={subId}/>
              ))}
            </div>
          )}

          {/* Product-level notes */}
          {notes&&(
            <div style={{ background:"#fff",borderRadius:"12px",border:"1px solid #E5E7EB",
              padding:"14px 16px",boxShadow:"0 1px 4px rgba(0,0,0,0.04)" }}>
              <div style={{ fontSize:"11px",fontWeight:"700",color:"#9CA3AF",
                textTransform:"uppercase",letterSpacing:"0.6px",marginBottom:"8px" }}>
                Overview
              </div>
              <div style={{ fontSize:"13px",color:"#374151",lineHeight:1.75 }}
                dangerouslySetInnerHTML={{ __html:product.NOTES||"" }}/>
            </div>
          )}
        </div>

        {/* RIGHT: image + meta + tabs */}
        <div style={{ width:"280px",flexShrink:0,display:"flex",flexDirection:"column",
          overflow:"hidden",background:"#F8FAFC",borderLeft:"1px solid #E5E7EB" }}>

          {/* Image strip */}
          {images.length>0&&(
            <div style={{ padding:"12px 12px 8px",borderBottom:"1px solid #E5E7EB",
              background:"#fff",flexShrink:0 }}>

            </div>
          )}

          {/* Meta */}
          <div style={{ padding:"10px 14px",borderBottom:"1px solid #E5E7EB",
            flexShrink:0,background:"#fff" }}>
            {[
              { label:"By",        val:product.FIRSTNAME },
            ].filter(r=>r.val).map((r,i)=>(
              <div key={i} style={{ display:"flex",gap:"8px",fontSize:"11px",
                marginBottom:"5px",alignItems:"flex-start" }}>
                <span style={{ color:"#9CA3AF",fontWeight:"600",minWidth:"62px",flexShrink:0 }}>
                  {r.label}
                </span>
                <span style={{ color:"#374151",lineHeight:1.4 }}>{r.val}</span>
              </div>
            ))}
            {product.LINK&&(
              <div style={{ display:"flex",gap:"8px",fontSize:"11px",alignItems:"flex-start" }}>
                <span style={{ color:"#9CA3AF",fontWeight:"600",minWidth:"62px",flexShrink:0 }}>
                  Source
                </span>
                <SourceLinks html={product.LINK}/>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div style={{ flex:1,display:"flex",flexDirection:"column",overflow:"hidden" }}>
            <div style={{ display:"flex",borderBottom:"1px solid #E5E7EB",
              flexShrink:0,background:"#fff" }}>
              {["attachments","comments","polls"].map(t=>(
                <button key={t} onClick={()=>setTab(t)}
                  style={{ padding:"7px 12px",border:"none",background:"none",cursor:"pointer",
                    fontSize:"11px",fontWeight:tab===t?"700":"500",
                    color:tab===t?tPrimary:"#6B7280",
                    borderBottom:`2px solid ${tab===t?tPrimary:"transparent"}`,
                    textTransform:"capitalize",transition:"all 0.15s" }}>
                  {t}
                </button>
              ))}
            </div>
            <div style={{ flex:1,overflowY:"auto" }}>
              {tab==="attachments"&&(
                files.length>0
                  ?<div style={{ padding:"10px 12px",display:"flex",flexWrap:"wrap",gap:"6px" }}>
                    {files.map((f,i)=><FilePill key={i} filepath={f.FILEPATH}/>)}
                  </div>
                  :<div style={{ padding:"2rem",textAlign:"center",color:"#9CA3AF",
                    fontSize:"12px" }}>No attachments</div>
              )}
              {tab==="comments"&&<CommentsPanel user={user} activeSub={activeSub} documentId={docId}/>}
              {tab==="polls"&&<CommentsPanel user={user} activeSub={activeSub} documentId={docId} pollOnly={true}/>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}